import TabPanel, { Item } from "devextreme-react/tab-panel";
import "./TemplateConfigEditor.css";
import React, { useEffect, useState } from "react";
import { getWorkflowConfigOptions } from "../../api/v2/TasklistsApi";
import { DefaultPhaseDto } from "../../dtos/DefaultPhaseDto";
import { DefaultStepDto } from "../../dtos/DefaultStepDto";
import { GrantDto } from "../../dtos/GrantDto";
import { PhaseWorkflowType } from "../../dtos/PhaseWorkflowType";
import { WorkflowConfigOptionsDto } from "../../dtos/WorkflowConfigOptionsDto";
import TemplateSummaryTab from "./tabs/TemplateSummaryTab";
import TemplateDetailsTab from "./tabs/TemplateDetailsTab";
import TemplateSettingsTab from "./tabs/TemplateSettingsTab";
import TemplatePermissionsTab from "./tabs/TemplatePermissionsTab";
import TemplateActionsTab from "./tabs/TemplateActionsTab";
import TemplateTransitionsTab from "./tabs/TemplateTransitionsTab";
import TemplateNotificationsTab from "./tabs/TemplateNotificationsTab";
import TemplateAudiencesTab from "./tabs/TemplateAudiencesTab";
import TemplateTriggersTab from "./tabs/TemplateTriggersTab";
import TemplateFilesTab from "./tabs/TemplateFilesTab";
import { StepWorkflowType } from "../../dtos/StepWorkflowType";
import { takesConfiguredDocuments } from "../shared/Utils";

export type TemplateConfigEditorProps = {
    // Either a task or subtask is being edited
    task?: DefaultPhaseDto | null;
    subtask?: DefaultStepDto | null;
    // Effective workflow type used by the existing tabs (resolved per-subtask under a hybrid parent)
    workflowType: PhaseWorkflowType;
    // The subtask's own workflow type, for the tabs whose rule is about the subtask rather than the
    // task. When a task is being edited this is what its new subtasks would get.
    subtaskWorkflowType?: StepWorkflowType | null;
    // Parent task's declared workflow type (Phase/Step/None/PhaseHybrid/StepHybrid). When the
    // parent is hybrid, the Settings tab exposes a per-subtask workflow picker.
    parentWorkflowType?: PhaseWorkflowType;
    // The parent task's flag: its subtasks hold In Progress only.
    uploadOnlySubtasks?: boolean;
    grantData: GrantDto;
    xsrfToken: string;
    onSave: (data: DefaultPhaseDto | DefaultStepDto) => Promise<void>;
    onClose: () => void;
    onDeleteSubtask?: () => Promise<void>;
    allPhaseTypes?: DefaultPhaseDto[];
};

// Which tab to show for a new selection: the one the user was reading if it exists here, the
// summary otherwise.
export function resolveTabIndex(titles: string[], remembered: string | null): number {
    const titleIndex = remembered === null ? -1 : titles.indexOf(remembered);
    if (titleIndex >= 0) return titleIndex;
    return Math.max(titles.indexOf("Summary"), 0);
}

export default function TemplateConfigEditor({
    task,
    subtask,
    workflowType,
    subtaskWorkflowType,
    parentWorkflowType,
    uploadOnlySubtasks,
    grantData,
    xsrfToken,
    onSave,
    onClose,
    onDeleteSubtask,
    allPhaseTypes
}: TemplateConfigEditorProps) {
    const [configOptions, setConfigOptions] = useState<WorkflowConfigOptionsDto | null>(null);
    const [loading, setLoading] = useState(true);
    // Remembered by name, not index: the tabs differ between a task and a subtask, so the same
    // index is a different tab after the selection changes.
    const [selectedTabTitle, setSelectedTabTitle] = useState<string | null>(null);

    const isTask = !!task;
    const isSubtask = !!subtask;
    const editingItem = task ?? subtask;

    useEffect(() => {
        (async () => {
            setLoading(true);
            // Pass the parent's flag so the backend can narrow an upload-only task's subtask options.
            const result = await getWorkflowConfigOptions(workflowType, task?.id, uploadOnlySubtasks, subtask?.id);
            if (result?.data) {
                setConfigOptions(result.data);
            }
            setLoading(false);
        })();
    }, [workflowType, task?.id, uploadOnlySubtasks, subtask?.id]);

    if (loading) {
        return <div className="p-4">Loading configuration options...</div>;
    }

    if (!configOptions) {
        return <div className="p-4">Failed to load configuration options.</div>;
    }

    if (!editingItem) {
        return <div className="p-4">Select a task or subtask to edit.</div>;
    }

    // Build tabs based on whether editing task or subtask
    // Configured documents need a subtask with a single file area - a document names one file, and a
    // subtask with separate Request and Response areas gives it two possible homes with no way to
    // say which. Both the subtask tab and the task's new-subtask defaults ask the same question of
    // the same value: the subtask's workflow type. Note this is the EFFECTIVE type - only a hybrid
    // task lets a subtask store its own, so what the subtask holds is not the answer on its own.
    const takesDocuments = takesConfiguredDocuments(subtaskWorkflowType);

    const tabs: { title: string; component: React.ReactNode }[] = [];

    if (isTask) {
        // TASK TABS:
        // 1. Summary - shows task summary with subtask list
        tabs.push({
            title: "Summary",
            component: (
                <TemplateSummaryTab
                    task={task}
                    subtask={null}
                    grantData={grantData}
                    configOptions={configOptions}
                    onClose={onClose}
                />
            )
        });

        // 2. Settings - all editable task settings (name, abbrev, key, dates, etc.)
        tabs.push({
            title: "Settings",
            component: (
                <TemplateSettingsTab
                    task={task}
                    subtask={null}
                    configOptions={configOptions}
                    onSave={onSave}
                />
            )
        });

        // 3. Permissions
        tabs.push({
            title: "Permissions",
            component: (
                <TemplatePermissionsTab
                    task={task}
                    subtask={null}
                    configOptions={configOptions}
                    onSave={onSave}
                    workflowType={workflowType}
                />
            )
        });

        // 4. Actions
        tabs.push({
            title: "Actions",
            component: (
                <TemplateActionsTab
                    task={task!}
                    configOptions={configOptions}
                    onSave={onSave}
                    workflowType={workflowType}
                />
            )
        });

        // 5. Notifications (not applicable for Step or Step Hybrid - each subtask operates
        // independently and the task has no status changes of its own to notify on)
        if (workflowType !== PhaseWorkflowType.Step && workflowType !== PhaseWorkflowType.StepHybrid) {
            tabs.push({
                title: "Notifications",
                component: (
                    <TemplateNotificationsTab
                        task={task}
                        subtask={null}
                        workflowType={workflowType}
                        configOptions={configOptions}
                        onSave={onSave}
                        mode="task"
                    />
                )
            });
        }

        // 6. Audiences
        tabs.push({
            title: "Audiences",
            component: (
                <TemplateAudiencesTab
                    task={task!}
                    configOptions={configOptions}
                    onSave={onSave}
                />
            )
        });

        // 7. Triggers
        tabs.push({
            title: "Triggers",
            component: (
                <TemplateTriggersTab
                    task={task!}
                    subtask={null}
                    allPhaseTypes={allPhaseTypes ?? []}
                    workflowType={workflowType}
                    onSave={onSave}
                />
            )
        });

        // 8. Subtask Permissions (defaults for new subtasks)
        tabs.push({
            title: "Subtask Permissions",
            component: (
                <TemplatePermissionsTab
                    task={task}
                    subtask={null}
                    configOptions={configOptions}
                    onSave={onSave}
                    mode="defaultSubtask"
                />
            )
        });

        // 8. Subtask Transitions (defaults for new subtasks)
        tabs.push({
            title: "Subtask Transitions",
            component: (
                <TemplateTransitionsTab
                    task={task}
                    subtask={null}
                    configOptions={configOptions}
                    onSave={onSave}
                    mode="defaultSubtask"
                />
            )
        });

        // 8c. Subtask Files (defaults for new subtasks).
        tabs.push({
            title: "Subtask Files",
            component: (
                <TemplateFilesTab
                    task={task}
                    onSave={onSave}
                    mode="defaultSubtask"
                    allowConfiguredDocuments={takesDocuments}
                    configOptions={configOptions}
                />
            )
        });

        // 9. Subtask Notifications (defaults for new subtasks)
        tabs.push({
            title: "Subtask Notifications",
            component: (
                <TemplateNotificationsTab
                    task={task}
                    subtask={null}
                    workflowType={workflowType}
                    configOptions={configOptions}
                    onSave={onSave}
                    mode="defaultSubtask"
                />
            )
        });
    }

    if (isSubtask) {
        // Check if this is a new (unsaved) subtask
        const isNewSubtask = subtask?.id === 0;

        // For new subtasks, only show Details tab until saved
        if (isNewSubtask) {
            tabs.push({
                title: "Details",
                component: (
                    <TemplateDetailsTab
                        task={null}
                        subtask={subtask}
                        workflowType={workflowType}
                        parentWorkflowType={parentWorkflowType}
                        grantData={grantData}
                        xsrfToken={xsrfToken}
                        onSave={onSave}
                    />
                )
            });
        } else {
            // SUBTASK TABS (for existing subtasks):
            // 1. Summary - readable view of subtask settings/config
            tabs.push({
                title: "Summary",
                component: (
                    <TemplateSummaryTab
                        task={null}
                        subtask={subtask}
                        grantData={grantData}
                        configOptions={configOptions}
                        onClose={onClose}
                        onDelete={onDeleteSubtask}
                    />
                )
            });

            // 2. Details - editable fields (name, description, request, files)
            tabs.push({
                title: "Details",
                component: (
                    <TemplateDetailsTab
                        task={null}
                        subtask={subtask}
                        workflowType={workflowType}
                        parentWorkflowType={parentWorkflowType}
                        grantData={grantData}
                        xsrfToken={xsrfToken}
                        onSave={onSave}
                    />
                )
            });

            // 2b. Files - documents and upload rules. Offered for every workflow type: the upload
            // limit and "accepts anything" apply to any subtask. Only the documents are gated.
            tabs.push({
                title: "Files",
                component: (
                    <TemplateFilesTab
                        subtask={subtask}
                        onSave={onSave}
                        mode="subtask"
                        allowConfiguredDocuments={takesDocuments}
                        configOptions={configOptions}
                    />
                )
            });

            // 3. Permissions
            tabs.push({
                title: "Permissions",
                component: (
                    <TemplatePermissionsTab
                        task={null}
                        subtask={subtask}
                        configOptions={configOptions}
                        onSave={onSave}
                    />
                )
            });

            // 4. Transitions
            tabs.push({
                title: "Transitions",
                component: (
                    <TemplateTransitionsTab
                        subtask={subtask!}
                        configOptions={configOptions}
                        onSave={onSave}
                    />
                )
            });

            // 5. Notifications
            tabs.push({
                title: "Notifications",
                component: (
                    <TemplateNotificationsTab
                        task={null}
                        subtask={subtask}
                        workflowType={workflowType}
                        configOptions={configOptions}
                        onSave={onSave}
                        mode="subtask"
                    />
                )
            });

            // 6. Triggers
            tabs.push({
                title: "Triggers",
                component: (
                    <TemplateTriggersTab
                        task={null}
                        subtask={subtask!}
                        allPhaseTypes={allPhaseTypes ?? []}
                        workflowType={workflowType}
                        onSave={onSave}
                    />
                )
            });
        }
    }

    // Stay on the tab the user was reading when they pick another task or subtask
    const selectedIndex = resolveTabIndex(tabs.map(t => t.title), selectedTabTitle);

    return (
        <div className="template-config-editor" style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
            {/* The scrolling now happens inside each tab, below the tab strip, so this only has to
                hand the TabPanel the full height and get out of the way. */}
            <div style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
                <TabPanel
                    height="100%"
                    animationEnabled={false}
                    swipeEnabled={false}
                    selectedIndex={selectedIndex}
                    onSelectedIndexChange={(index: number) => setSelectedTabTitle(tabs[index]?.title ?? null)}
                    // The strip scrolls on its own once the tabs outrun it (scrollingEnabled is on
                    // by default); this adds the arrows at each end, which otherwise only appear
                    // when asked for. Nothing renders until the tabs actually overflow.
                    showNavButtons={true}
                >
                    {tabs.map((tab, index) => (
                        <Item key={index} title={tab.title}>
                            {tab.component}
                        </Item>
                    ))}
                </TabPanel>
            </div>
        </div>
    );
}
